import { SHAvatar, SHStack, SHTypography } from "@components/design-systems";
import { StatusBadge } from "@components/status-badge";
import { PageRoutes } from "@constants";
import { DateFormat, DateTimeFormat } from "@constants/format";
import {
  AnalysisListDTO,
  AnalysisUserType,
} from "@models/platform-analysis/entities/analysis";
import { AnalysisStatus } from "@models/platform-analysis/enums/status";
import { AnalysisConfigs as Configs } from "@pages/platform-analysis-v2/configs";
import { TextOverflowEllipsis } from "@pages/platform-analysis/components/text-overflow-ellipsis";
import { PlatformAnalysisFilterOptions } from "@pages/platform-analysis/constant";
import {
  CellContext,
  ColumnDef,
  ColumnMeta,
  createColumnHelper,
} from "@tanstack/react-table";
import { nameOfFactory } from "@utils";
import { format } from "date-fns";
import React from "react";
import { generatePath } from "react-router";

export const AnalysisColumnHelper = createColumnHelper<AnalysisListDTO>();
export const AnalysisNameOfFactory = nameOfFactory<AnalysisListDTO>();

export interface GetAnalysisTableColumnDefsProps {
  userType?: AnalysisUserType;
  isSupplierBdmSale?: boolean;

  renderItemActions?: (itemData: AnalysisListDTO) => React.ReactNode;
}

export const getAnalysisTableColumnDefs = ({
  userType,
  isSupplierBdmSale,
  renderItemActions,
}: GetAnalysisTableColumnDefsProps): ColumnDef<AnalysisListDTO, any>[] => {
  if (!renderItemActions || !userType) return [];

  return getColumnDefs({
    isSupplierBdmSale,
    renderItemActions,
    userType,
  });
};

export const getPracticeOverviewNavigateRoute = (firmId?: string) =>
  `${generatePath(PageRoutes.practiceOverview, {
    practiceId: firmId,
  })}/subscription`;

// region private methods
const renderDateTime = (
  props: CellContext<AnalysisListDTO, any>,
  dateFormat?: string,
) => {
  const date = props.cell.getValue();
  return date ? format(new Date(date), `${dateFormat}`) : "";
};

const ColumnDefsKeys: Map<
  keyof AnalysisListDTO,
  {
    showFor: AnalysisUserType[];
    showForSupplierBDMSaleOnly?: boolean;
    header: { supplier?: string; adviser?: string };
    enableColumnFilter: boolean;
    renderCell: (
      props: CellContext<AnalysisListDTO, any>,
    ) => JSX.Element | string;
    metaData: (
      useForSupplier?: boolean,
      isSupplierBdmSale?: boolean,
    ) => ColumnMeta<AnalysisListDTO, any>;
  }
> = new Map([
  [
    "name",
    {
      showFor: ["supplier", "adviser"],
      header: { supplier: "Analysis name", adviser: "Name of review" },
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) => (
        <TextOverflowEllipsis fontWeight="bold" value={props.cell.getValue()} />
      ),
      metaData: (useForSupplier?: boolean, isSupplierBdmSale?: boolean) => ({
        sx: {
          width: useForSupplier
            ? isSupplierBdmSale
              ? "22%"
              : { xs: "22%", md: "36%" }
            : "25%",
          minWidth: !useForSupplier ? "200px" : undefined,
          padding: "0 16px !important",
        },
      }),
    },
  ],
  [
    "familyGroupName",
    {
      showFor: ["adviser"],
      header: { adviser: "Family group" },
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) => (
        <SHTypography>{props.cell.getValue()}</SHTypography>
      ),
      metaData: () => ({
        sx: {
          minWidth: "155px",
          width: "15%",
        },
      }),
    },
  ],
  [
    "ownerName",
    {
      showFor: ["supplier", "adviser"],
      header: { supplier: "Owner", adviser: "Adviser" },
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) => (
        <SHStack direction={"row"} alignItems={"center"}>
          <SHAvatar
            width={"24px"}
            height={"24px"}
            src={props.row.original.ownerAvatarUrl}
          />
          <TextOverflowEllipsis
            value={props.cell.getValue()}
            sx={{
              marginLeft: "5px",
              minWidth: "95%",
            }}
          />
        </SHStack>
      ),
      metaData: (useForSupplier?: boolean, isSupplierBdmSale?: boolean) => ({
        sx: {
          width: isSupplierBdmSale ? "12%" : "15%",
          padding: "0 16px !important",
          marginLeft: !useForSupplier ? "5px" : undefined,
        },
      }),
    },
  ],
  [
    "adviserName",
    {
      header: { supplier: "Adviser name" },
      showFor: ["supplier"],
      showForSupplierBDMSaleOnly: true,
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) => (
        <SHTypography>{props.cell.getValue()}</SHTypography>
      ),
      metaData: () => ({
        sx: {
          width: "14%",
        },
      }),
    },
  ],
  [
    "includeSteps",
    {
      showFor: ["supplier"],
      header: { supplier: "What's included" },
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) => (
        <SHTypography fontSize={Configs.DefaultFontSize} lineHeight={"120%"}>
          {props.cell.getValue()}
        </SHTypography>
      ),
      metaData: () => ({
        sx: {
          width: "15%",
          minWidth: { xs: "100px", md: "160px" },
          padding: "0 16px !important",
        },
      }),
    },
  ],
  [
    "dataValidDate",
    {
      showFor: ["supplier", "adviser"],
      header: { supplier: "Data valid", adviser: "Data valid" },
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) =>
        renderDateTime(props, DateFormat),
      metaData: () => ({
        sx: {
          width: "11%",
          padding: "0 16px !important",
        },
      }),
    },
  ],
  [
    "lastModifiedDate",
    {
      showFor: ["supplier", "adviser"],
      header: { supplier: "Last edit", adviser: "Last edit" },
      enableColumnFilter: false,
      renderCell: (props: CellContext<AnalysisListDTO, any>) =>
        renderDateTime(props, DateTimeFormat),
      metaData: () => ({
        sx: {
          width: { xs: "10%", md: "12%" },
          padding: "0 16px !important",
        },
      }),
    },
  ],
  [
    "status",
    {
      showFor: ["supplier", "adviser"],
      header: { supplier: "Status", adviser: "Status" },
      enableColumnFilter: true,
      renderCell: (props: CellContext<AnalysisListDTO, any>) => (
        <StatusBadge
          style={{ width: "101px", fontSize: Configs.DefaultFontSize }}
          status={props.cell.getValue() as AnalysisStatus}
        />
      ),
      metaData: () => ({
        filterData: PlatformAnalysisFilterOptions,
        sx: {
          width: "9%",
          padding: "0 16px !important",
        },
      }),
    },
  ],
]);

const getColumnDefs = ({
  isSupplierBdmSale,
  renderItemActions,
  userType,
}: GetAnalysisTableColumnDefsProps): ColumnDef<AnalysisListDTO, any>[] => {
  const cols: ColumnDef<AnalysisListDTO, any>[] = [];

  if (!renderItemActions || !userType) return cols;

  ColumnDefsKeys.forEach((configs, key) => {
    const {
      header: { supplier: supplierHeader, adviser: adviserHeader },
      showFor,
      showForSupplierBDMSaleOnly,
      enableColumnFilter,
      renderCell,
      metaData,
    } = configs;

    if (userType && showFor.indexOf(userType) === -1) return;
    if (showForSupplierBDMSaleOnly && !isSupplierBdmSale) return;

    const useForSupplier = userType === "supplier";
    const colAccessor = AnalysisColumnHelper.accessor(key, {
      id: AnalysisNameOfFactory(key),
      header: userType === "supplier" ? supplierHeader : adviserHeader,
      enableColumnFilter: enableColumnFilter,
      cell: renderCell,
      meta: metaData(useForSupplier, isSupplierBdmSale),
    });

    cols.push(colAccessor);
  });

  cols.push(
    AnalysisColumnHelper.accessor((row) => row, {
      id: "actionCol",
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (props) => renderItemActions(props.row.original),
      meta: {
        sx: {
          width: "10%",
        },
      },
    }),
  );

  return cols;
};
// endregion private methods
